import Queue from 'promise-queue';
import { ApiResponse, parseResult } from '@parcel2go/data';
import { DocCache } from '../cache/DocCache';

export type CacheItem<T> = {
  expiry: Date;
  added: Date;
  item: T;
};

type cacheType = 'SESSIONSTORAGE' | 'LOCALSTORAGE' | 'MEMORY';

async function getOrAdd<T>(
  queue: Queue,
  cacheType: cacheType,
  key: string,
  response: () => Promise<Response>,
  cacheMins?: number,
  parse?: ((json: any) => T) | null | undefined
): Promise<ApiResponse<T>> {
  // Ignore Queue if already cached
  const cached = getCached(cacheType, key);
  if (cached) {
    return Promise.resolve({
      statusCode: 200,
      success: true,
      result: cached as T,
      error: null,
      validationErrors: {}
    });
  }

  return queue.add(() => {
    // Check cache here if a previous call in the queue requested the same data
    const cached = getCached(cacheType, key);
    if (cached) {
      return Promise.resolve({
        statusCode: 200,
        success: true,
        result: cached as T,
        error: null,
        validationErrors: null
      });
    }
    return response().then((r) =>
      parseResult(r, parse).then((r) => {
        if (r.success) {
          addCache(cacheType, key, r.result, cacheMins);
        }
        return r;
      })
    );
  });
}

export function clearDocCacheKey(key: string) {
  DocCache[key] = null;
}

async function getOrAddToQueue<T>(
  queue: Queue,
  response: () => Promise<Response>,
  parse?: ((json: any) => T) | null | undefined
): Promise<ApiResponse<T>> {
  return queue.add(() => response().then((r) => parseResult(r, parse)));
}

export function getCached<T>(cachType: cacheType, key: string) {
  try {
    if (cachType === 'LOCALSTORAGE' && localStorage) {
      const cached = localStorage.getItem(key);
      if (cached) {
        const item: CacheItem<T> = JSON.parse(cached);
        if (new Date(item.expiry) > new Date()) {
          return item.item;
        }
        localStorage.removeItem(key);
      }
    } else if (cachType === 'SESSIONSTORAGE' && sessionStorage) {
      const cached = sessionStorage.getItem(key);
      if (cached) {
        const item: CacheItem<T> = JSON.parse(cached);
        if (new Date(item.expiry) > new Date()) {
          return item.item;
        }
        sessionStorage.removeItem(key);
      }
    } else if (cachType === 'MEMORY') {
      const cached = DocCache[key];
      if (cached) {
        const item: CacheItem<T> = cached;
        if (new Date(item.expiry) > new Date()) {
          return item.item;
        }
        DocCache[key] = null;
      }
    }
  } catch (e) {
    console.log(e);
  }
  return null;
}

export function addCache<T>(cacheType: cacheType, key: string, item: T, cacheMins?: number) {
  const cached: CacheItem<T> = {
    expiry: new Date(new Date().getTime() + (cacheMins || 60) * 60000),
    added: new Date(),
    item: item
  };
  try {
    if (cacheType === 'LOCALSTORAGE' && localStorage) {
      localStorage.setItem(key, JSON.stringify(cached));
    } else if (cacheType === 'SESSIONSTORAGE' && sessionStorage) {
      sessionStorage.setItem(key, JSON.stringify(cached));
    } else if (cacheType === 'MEMORY') {
      DocCache[key] = cached;
    }
  } catch (e) {
    console.log(e);
  }
}

function clearItemCache(cacheType: cacheType, key: string) {
  if (cacheType === 'LOCALSTORAGE' && localStorage && localStorage.getItem(key)) {
    localStorage.removeItem(key);
  } else if (cacheType === 'SESSIONSTORAGE' && sessionStorage && sessionStorage.getItem(key)) {
    sessionStorage.removeItem(key);
  } else if (cacheType === 'MEMORY') {
    DocCache[key] = null;
  }
}

export { getOrAdd, getOrAddToQueue, Queue, clearItemCache };
